import { lazy, Suspense, useEffect } from 'react';
import { RouteLoading } from './RouteLoading';
import { useSession } from '../features/auth';
import { navigate } from './navigation';
import { adminResourcePath, canAccessRoute, type AppRoute } from './routes';
import { CMS_NAME, PRODUCT_BRAND, VIEW_NAME } from '../shared/product';
const PublicDashboardRoute = lazy(() =>
  import('./PublicDashboardRoute').then((module) => ({ default: module.PublicDashboardRoute })),
);
const MapPage = lazy(() =>
  import('../pages/map/MapPage').then((module) => ({ default: module.MapPage })),
);
const AdminPage = lazy(() =>
  import('../pages/admin/AdminPage').then((module) => ({ default: module.AdminPage })),
);
const DocumentPreviewPage = lazy(() =>
  import('../features/project-viewer').then((module) => ({
    default: module.DocumentPreviewPage,
  })),
);
const IfcViewerPage = lazy(() =>
  import('../features/ifc-viewer').then((module) => ({ default: module.IfcViewerPage })),
);
/** Route composition is outside features; mount exactly one workspace/renderer owner. */
export function AppRouter({ route }: { route: AppRoute }) {
  const { user } = useSession();
  const allowed = canAccessRoute(route, user?.role);
  const title = !allowed
    ? 'Không có quyền truy cập'
    : route.area === 'admin'
      ? {
          overview: 'Tổng quan',
          imports: 'Nhập bộ hồ sơ',
          projects: 'Công trình',
          maintenance: 'Lịch bảo trì',
          models: 'Mô hình IFC',
          gis: 'Dữ liệu GIS',
          layers: 'Cấu hình lớp',
          users: 'Tài khoản',
          access: 'Nhóm & phân quyền',
          backups: 'Sao lưu & khôi phục',
          'audit-logs': 'Nhật ký thao tác',
          'system-logs': 'Nhật ký HTTP',
        }[route.section]
      : route.area === 'map'
        ? VIEW_NAME
        : route.area === 'dashboard'
          ? 'Tổng quan công trình & vận hành'
          : route.area === 'ifc-viewer'
            ? 'Trình xem IFC'
            : route.area === 'document-preview'
              ? 'Xem tài liệu'
              : route.area === 'login'
                ? 'Đăng nhập'
                : 'Không tìm thấy trang';
  useEffect(() => {
    if (route.area === 'login' && user) navigate('/');
  }, [route.area, user]);
  useEffect(() => {
    document.title =
      allowed && route.area === 'map'
        ? VIEW_NAME
        : `${title} · ${route.area === 'admin' ? CMS_NAME : PRODUCT_BRAND}`;
  }, [title, allowed, route.area]);
  if (route.area === 'login')
    return (
      <main className="auth-screen">
        <p role="status">Đang mở bản đồ…</p>
      </main>
    );
  if (!allowed)
    return (
      <RouteMessage
        title="Khu vực dành cho quản trị viên"
        message="Tài khoản của bạn không có quyền truy cập trang quản trị."
      />
    );
  return (
    <Suspense fallback={<RouteLoading route={route} />}>
      {route.area === 'admin' ? (
        <AdminPage
          section={route.section}
          resource={route.resource}
          onResourceNavigate={(location) =>
            navigate(adminResourcePath(route.section, location), { confirmed: true })
          }
          onNavigate={navigate}
          onModelOpen={(id) =>
            navigate(`/view/models/${encodeURIComponent(id)}`, { confirmed: true })
          }
        />
      ) : route.area === 'dashboard' ? (
        <PublicDashboardRoute route={route} onNavigate={navigate} />
      ) : route.area === 'map' ? (
        <MapPage
          key="map-workspace"
          initialModelId={route.modelId}
          initialProjectId={route.projectId}
          projectTab={route.projectTab}
          onNavigate={navigate}
        />
      ) : route.area === 'ifc-viewer' ? (
        <IfcViewerPage modelId={route.modelId} />
      ) : route.area === 'document-preview' ? (
        <DocumentPreviewPage
          projectId={route.projectId}
          documentId={route.documentId}
          revisionId={route.revisionId}
        />
      ) : (
        <RouteMessage title="Không tìm thấy trang" message="Đường dẫn này không tồn tại." />
      )}
    </Suspense>
  );
}
function RouteMessage({ title, message }: { title: string; message: string }) {
  return (
    <main className="auth-screen">
      <section className="auth-card">
        <h1>{title}</h1>
        <p>{message}</p>
        <a
          className="button primary"
          href="/"
          onClick={(event) => {
            event.preventDefault();
            navigate('/');
          }}
        >
          Về bản đồ
        </a>
      </section>
    </main>
  );
}
